/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react-hooks/rules-of-hooks */
import { RoleFunction } from '@eagle/common';
import { DashboardType } from '@eagle/core-data-types';
import {
  AuthStatus,
  ContentCategoryTracking,
  hasPortalAccess,
  MicroPortalAuthenticate,
  PROFILE_PAGE_CREATE_SUBSCRIPTIONS_FLAG,
  T_MANY,
  T_ONE,
  TemplatedPage,
  useAuth,
  useCustomRoutes,
  useFlags,
  useHasAuthorization
} from '@eagle/react-common';
import { useMemo, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import {
  AlertDetailPage,
  AlertListPage,
  CameraContentPage,
  DashboardDetailPage,
  DashboardPage,
  DashboardsListPage,
  EventHistoryPage,
  OverviewPage,
  PersonDetailPage,
  PersonListPage,
  ProfilePage,
  ReportsListPage,
  ReportsPage,
  SpareFleetCapacityReportPage,
  SubscriptionCreatePage,
  SubscriptionDetailPage,
  ThingDetailPage,
  ThingListPage,
  ThingMapPage,
  TripHistoryBaseViewPage
} from './pages';
import { DASHBOARD_DETAIL_FLAG } from './pages/dashboard-detail/dashboard-detail';
import { SPARE_FLEET_CAPACITY_FLAG } from './pages/reports-spare-fleet-capacity/reports-spare-fleet-capacity';
import { Welcome } from './pages/welcome/welcome';

export interface RouteInfo {
  icon?: JSX.Element;
  key?: string;
  page: JSX.Element;
  path: string;
  subText?: string;
  text?: string;
  googleAnalyticsCategory?: ContentCategoryTracking;
}

const USER_ROLE = [RoleFunction.USER] as const;

const ALERT_PERMISSIONS = [RoleFunction.ALERT_VIEWER] as const;
const MEDIA_PERMISSIONS = [RoleFunction.MEDIA_VIEWER] as const;
const SYSTEM_ADMIN_ROLE = [RoleFunction.SYSTEM_ADMINISTRATOR] as const;

export const useRoutes = (): RouteInfo[] => {
  const { state } = useAuth();
  const { t } = useTranslation(['common', 'terms', 'track']);
  const flags = useFlags();
  const {
    alert,
    alerts,
    dashboards,
    history,
    people,
    person,
    reports,
    subscription,
    thing,
    things,
  } = useCustomRoutes();
  const { hasAuthorization } = useHasAuthorization(state);

  const alertPermissions = hasAuthorization(ALERT_PERMISSIONS);
  const mediaPermissions = hasAuthorization(MEDIA_PERMISSIONS);
  const systemAdminPermissions = hasAuthorization(SYSTEM_ADMIN_ROLE);

  if (!flags) return [];

  return useMemo<RouteInfo[]>(() => {
    console.log('Calculating routes');

    if (!state || state.status !== AuthStatus.AUTHENTICATED) return [];
    const { account } = state;

    const hasBasicAuthorization = hasAuthorization(USER_ROLE) && hasPortalAccess('track', hasAuthorization);

    const commonRoutes: RouteInfo[] = [];

    if (flags['portals-profile-feature']) {
      commonRoutes.push({
        page: <ProfilePage />,
        path: '/profile',
        googleAnalyticsCategory: ContentCategoryTracking.UserProfile,
      });

      commonRoutes.push({
        page: <ProfilePage />,
        path: `/profile/${subscription}`,
        googleAnalyticsCategory: ContentCategoryTracking.UserProfile,
      });

      commonRoutes.push({
        page: <ProfilePage />,
        path: '/profile/terms-and-conditions',
      });

      commonRoutes.push({
        page: <SubscriptionDetailPage isProfilePage />,
        path: `/profile/${subscription}/:subscriptionId`,
      });

      if (flags[PROFILE_PAGE_CREATE_SUBSCRIPTIONS_FLAG as string]) {
        commonRoutes.push({
          page: <SubscriptionCreatePage isProfilePage />,
          path: `/profile/${subscription}/create`,
        });
      }
    }

    if (!hasBasicAuthorization) {
      const noPermissionRoutes: RouteInfo[] = [{
        page: <Welcome />,
        path: '/',
      }];

      return [...noPermissionRoutes, ...commonRoutes];
    }

    const routes: RouteInfo[] = [];
    const overviewDashboardId = String(flags['track-dashboard-sisense-id']) || account.config?.portals?.tracking?.dashboard?.sisenseId;
    routes.push({
      page: flags['track-overview-feature'] && overviewDashboardId
        ? <OverviewPage dashboardId={overviewDashboardId} />
        : <Navigate to={`/map/${things}`} />,
      path: '/',
      text: t('track:page.thing-map.title'),
      googleAnalyticsCategory: ContentCategoryTracking.Map,
    });

    if (flags['track-reports-feature']) {
      routes.push({
        page: <ReportsListPage />,
        path: `/${reports}`,
        text: t('track:page.reports.title'),
      });

      if (hasBasicAuthorization && flags[SPARE_FLEET_CAPACITY_FLAG]) {
        routes.push({
          page: <SpareFleetCapacityReportPage />,
          path: `/${reports}/spare-fleet-capacity`,
          text: t('track:page.reports.spare-fleet-capacity.title'),
          googleAnalyticsCategory: ContentCategoryTracking.Reports,
        });
      }

      routes.push({
        page: <ReportsPage />,
        path: `/${reports}/:dashboardId`,
        text: t('track:page.reports.title'),
        googleAnalyticsCategory: ContentCategoryTracking.Reports,
      });
    }

    if (systemAdminPermissions && flags[DASHBOARD_DETAIL_FLAG]) {
      routes.push({
        page: <DashboardDetailPage pageType={DashboardType.REPORT} />,
        path: `/${reports}/manage/:dashboardId`,
        text: t('track:page.dashboard-detail.dashboard-details.sub-title'),
        googleAnalyticsCategory: ContentCategoryTracking.Dashboards,
      });

      routes.push({
        page: <DashboardDetailPage pageType={DashboardType.DASHBOARD} />,
        path: `/${dashboards}/manage/:dashboardId`,
        text: t('track:page.dashboard-detail.dashboard-details.sub-title'),
        googleAnalyticsCategory: ContentCategoryTracking.Dashboards,
      });
    }

    if (flags['track-dashboards-feature']) {
      routes.push({
        page: <DashboardsListPage />,
        path: '/dashboards',
        text: t('track:page.dashboards.title'),
      });

      routes.push({
        page: <DashboardPage />,
        path: '/dashboards/:dashboardId',
        text: t('track:page.dashboards.title'),
        googleAnalyticsCategory: ContentCategoryTracking.Dashboards,
      });
    }

    routes.push({
      key: 'thing-map',
      page: <ThingMapPage />,
      path: `/map/${things}`,
      text: t('track:page.thing-map.title'),
      googleAnalyticsCategory: ContentCategoryTracking.Map,
    });

    routes.push({
      key: 'thing-map',
      page: <ThingMapPage />,
      path: `/map/${things}/:thingId`,
      text: t('track:page.thing-map.title'),
      googleAnalyticsCategory: ContentCategoryTracking.Map,
    });

    routes.push({
      page: <ThingMapPage />, // TODO TP-???: Person map
      path: `/map/${people}`,
      text: t('common:common.labels.map'),
      googleAnalyticsCategory: ContentCategoryTracking.Map,
    });

    routes.push({
      page: <ThingListPage />,
      path: `/${things}`,
      text: t('terms:thing', { count: T_MANY }),
      googleAnalyticsCategory: ContentCategoryTracking.Things,
    });

    routes.push({
      page: <ThingDetailPage useDataDrivenEventList={Boolean(flags['track-event-card-data-driven-feature'])} />,
      path: `/${thing}/:thingId`,
      text: t('terms:thing', { count: T_ONE }),
      googleAnalyticsCategory: ContentCategoryTracking.Things,
    });

    routes.push({
      page: <PersonListPage />,
      path: `/${people}`,
      text: t('terms:person', { count: T_MANY }),
      googleAnalyticsCategory: ContentCategoryTracking.People,
    });

    routes.push({
      page: <PersonDetailPage />,
      path: `/${person}/:personId`,
      text: t('terms:person', { count: T_ONE }),
      googleAnalyticsCategory: ContentCategoryTracking.People,
    });

    if (flags['track-history-trips-core-experience-temporary']) {
      routes.push({
        page: <TripHistoryBaseViewPage />,
        path: `/${history}`,
        text: t('common:common.labels.history'),
      });

      routes.push({
        page: <TripHistoryBaseViewPage />,
        path: `/${history}/:data`,
        text: t('common:common.labels.history'),
      });
    } else if (flags['track-thing-history-feature']) {
      routes.push({
        page: <EventHistoryPage />,
        path: `/${history}`,
        text: t('common:common.labels.history'),
      });

      routes.push({
        page: <EventHistoryPage />,
        path: `/${history}/:data`,
        text: t('common:common.labels.history'),
      });
    }

    if (mediaPermissions) {
      routes.push({
        page: <CameraContentPage data-testid='camera-content' />,
        path: '/camera/:id',
        text: t('track:page.camera-content.title'),
      });
    }

    if (alertPermissions) {
      routes.push({
        page: <AlertListPage />,
        path: `/${alerts}`,
        text: t('common:terms.alert', { count: T_MANY }),
        googleAnalyticsCategory: ContentCategoryTracking.Alerts,
      });

      routes.push({
        page: <AlertDetailPage />,
        path: `/${alert}/:alertId`,
        text: t('common:terms.alert', { count: T_ONE }),
        googleAnalyticsCategory: ContentCategoryTracking.Alerts,
      });
    }

    if (flags['portals-templated-page-feature']) {
      routes.push({
        page: <TemplatedPage portal="track" />,
        path: '/view/:templateId',
      });
    }

    routes.push({
      page: <MicroPortalAuthenticate />,
      path: '/micro-portal/authenticate/:app',
    });

    return [...routes, ...commonRoutes];
  }, [state, t]);
};
